// src/components/layout/TasksList.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  FileText,
  Calendar,
  Users,
  PlusCircle,
  Share2,
  Lock,
  AlertCircle,
  Grid3X3,
  List,
  ChevronDown,
  ChevronUp,
  X,
  Check,
  UserPlus,
  Copy
} from 'lucide-react';
import { AssessmentService } from '../../services/taskService';
import { AuthService } from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useAssessmentService } from '../../hooks/useTaskService';

const TasksList = ({ tasks = null, onTaskSelect, onEditTask, onCreateTask, task, onTaskListUpdate, selectedSubjectGrade = null }) => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [tasksData, setTasksData] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const { loading, error, getTasks, deleteTask, shareTask, unshareTask, duplicateTask, clearError } = useAssessmentService();
  
  // Filter and view states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterGrade, setFilterGrade] = useState('all');
  const [filterOwnership, setFilterOwnership] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  
  // Sharing states
  const [showSharingModal, setShowSharingModal] = useState(false);
  const [selectedTaskForSharing, setSelectedTaskForSharing] = useState(null);
  const [shareableUsers, setShareableUsers] = useState([]);
  const [sharingLoading, setSharingLoading] = useState(false);

  const [duplicatingTask, setDuplicatingTask] = useState(null);

  // Define isTaskOwner function
  const isTaskOwner = (task) => {
    return task?.createdBy === currentUser?.uid;
  };

  useEffect(() => {
    if (tasks) {
      setTasksData(tasks);
    } else {
      loadTasks();
    }
  }, [tasks]);

  useEffect(() => {
    if (userProfile) {
      loadShareableUsers();
    }
  }, [userProfile]);

  const loadTasks = async () => {
    try {
      const taskList = await getTasks();
      setTasksData(taskList);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
  };

  const loadShareableUsers = async () => {
    try {
      if (userProfile) {
        const users = await AuthService.getUsersForSharing(userProfile);
        setShareableUsers(users);
      }
    } catch (error) {
      console.error('Error loading shareable users:', error);
    }
  };

  const handleDeleteTask = async (taskId, taskName) => {
    if (window.confirm(`Are you sure you want to delete "${taskName}"?`)) {
      try {
        await deleteTask(taskId);
        setTasksData(prev => prev.filter(task => task.id !== taskId));
      } catch (err) {
        console.error('Failed to delete task:', err);
      }
    }
  };

  const handleShareTask = async (task) => {
    setSelectedTaskForSharing(task);
    setShowSharingModal(true);
  };

  const handleToggleUserShare = async (userId, isCurrentlyShared) => {
    if (!selectedTaskForSharing) return;

    setSharingLoading(true);
    try {
      if (isCurrentlyShared) {
        await unshareTask(selectedTaskForSharing.id, [userId]);
      } else {
        await shareTask(selectedTaskForSharing.id, [userId]);
      }
      
      // Update local task data
      setTasksData(prev => prev.map(task => {
        if (task.id === selectedTaskForSharing.id) {
          const updatedSharedWith = isCurrentlyShared
            ? task.sharedWith?.filter(id => id !== userId) || []
            : [...(task.sharedWith || []), userId];
          return { ...task, sharedWith: updatedSharedWith };
        }
        return task;
      }));

      // Update selected task for sharing modal
      setSelectedTaskForSharing(prev => {
        const updatedSharedWith = isCurrentlyShared
          ? prev.sharedWith?.filter(id => id !== userId) || []
          : [...(prev.sharedWith || []), userId];
        return { ...prev, sharedWith: updatedSharedWith };
      });

    } catch (error) {
      console.error('Error toggling share:', error);
    } finally {
      setSharingLoading(false);
    }
  };

  useEffect(() => {
    applyFilters();
  }, [tasks, searchTerm, filterType, filterGrade, filterOwnership]);

  const applyFilters = () => {
    let filtered = tasks.filter(task => {
      const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           task.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           task.topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesType = filterType === 'all' || task.type === filterType;
      const matchesGrade = filterGrade === 'all' || task.grade === filterGrade;
      
      const matchesOwnership = filterOwnership === 'all' || 
        (filterOwnership === 'owned' && task.createdBy === currentUser.uid) ||
        (filterOwnership === 'shared' && task.createdBy !== currentUser.uid);
      
      return matchesSearch && matchesType && matchesGrade && matchesOwnership;
    });
    
    setFilteredTasks(filtered);
  };

  const getUniqueGrades = () => {
    return [...new Set(tasks.map(task => task.grade))].sort();
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterGrade('all');
    setFilterOwnership('all');
  };

  // Alternative if onEditTask expects just an ID
  const handleDuplicateTask = async (task) => {
    try {
      setDuplicatingTask(task.id);
      const duplicatedTask = await duplicateTask(task.id);
      
      // Refresh the task list first
      if (!tasks) {
        await loadTasks();
      }
      
    } catch (err) {
      console.error('Failed to duplicate task:', err);
      alert('Failed to duplicate task. Please try again.');
    } finally {
      setDuplicatingTask(null);
      loadTasks()
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Tasks</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <button
          onClick={loadTasks}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6 w-full">
      {/* Header with inline controls */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Assessment Tasks
            {selectedSubjectGrade && (
              <span className="text-lg font-normal text-gray-600 ml-2">
                - {selectedSubjectGrade.display}
              </span>
            )}
          </h2>
          <p className="text-gray-600">
            {selectedSubjectGrade 
              ? `Manage tasks for ${selectedSubjectGrade.display}`
              : 'Manage and track your assessment tasks'
            }
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Create Task Button */}
          <button
            onClick={onCreateTask}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-purple-700 flex items-center gap-2"
          >
            <PlusCircle className="w-4 h-4" />
            Create Task
          </button>

          <div className="text-sm text-gray-500">
            {filteredTasks.length} of {tasks.length} tasks
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('card')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'card' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="Card view"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              title="List view"
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Filters Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Collapsible Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, subject, or topic..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="Assignment">Assignment</option>
                <option value="Project">Project</option>
                <option value="Investigation">Investigation</option>
                <option value="Test">Test</option>
                <option value="Exam">Exam</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grade</label>
              <select
                value={filterGrade}
                onChange={(e) => setFilterGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Grades</option>
                {getUniqueGrades().map(grade => (
                  <option key={grade} value={grade}>{grade}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Ownership</label>
              <select
                value={filterOwnership}
                onChange={(e) => setFilterOwnership(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Tasks</option>
                <option value="owned">My Tasks</option>
                <option value="shared">Shared with Me</option>
              </select>
            </div>
          </div>

          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              {(searchTerm || filterType !== 'all' || filterGrade !== 'all' || filterOwnership !== 'all') && (
                <button
                  onClick={clearAllFilters}
                  className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                >
                  Clear All
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(false)}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Hide Filters
            </button>
          </div>
        </div>
      )}

      {/* Tasks Display */}
      {viewMode === 'card' ? (
        /* Card View */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTasks.map(task => (
            <TaskCard 
              key={task.id}
              task={task}
              isTaskOwner={isTaskOwner}
              onTaskSelect={onTaskSelect}
              onEditTask={onEditTask}
              onDeleteTask={handleDeleteTask}
              onShareTask={handleShareTask}
              onDuplicateTask={handleDuplicateTask}
              duplicatingTask={duplicatingTask}
            />
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Task
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade/Subject
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Marks
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTasks.map(task => (
                  <TaskRow 
                    key={task.id}
                    task={task}
                    isTaskOwner={isTaskOwner}
                    onTaskSelect={onTaskSelect}
                    onEditTask={onEditTask}
                    onDeleteTask={handleDeleteTask}
                    onShareTask={handleShareTask}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredTasks.length === 0 && (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {tasks.length === 0 ? 'No assessment tasks yet' : 'No tasks match your filters'}
          </h3>
          <p className="text-gray-500 mb-6">
            {tasks.length === 0 
              ? 'Create your first assessment task to get started.'
              : 'Try adjusting your search terms or filters.'
            }
          </p>
          {tasks.length === 0 && (
            <button
              onClick={onCreateTask}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 mx-auto"
            >
              <PlusCircle className="w-4 h-4" />
              Create Your First Task
            </button>
          )}
        </div>
      )}
    
      {/* Sharing Modal */}
      {showSharingModal && selectedTaskForSharing && (
        <SharingModal
          task={selectedTaskForSharing}
          shareableUsers={shareableUsers}
          onClose={() => {
            setShowSharingModal(false);
            setSelectedTaskForSharing(null);
          }}
          onToggleShare={handleToggleUserShare}
          loading={sharingLoading}
        />
      )}
    </div>
  );
};

// Task Card Component
const TaskCard = ({ task, isTaskOwner, onTaskSelect, onEditTask, onDeleteTask, onDuplicateTask,
  duplicatingTask, onShareTask }) => (
  <div className="bg-blue-50 rounded-lg shadow-md hover:shadow transition-shadow">
    <div className="p-6">
      {/* Task Header */}
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-semibold text-gray-900">{task.name}</h3>
            {!isTaskOwner(task) && (
              <Share2 className="w-4 h-4 text-blue-500" title="Shared with you" />
            )}
            {isTaskOwner(task) && task.sharedWith?.length > 0 && (
              <Users className="w-4 h-4 text-green-500" title={`Shared with ${task.sharedWith.length} users`} />
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              task.type === 'Exam' ? 'bg-red-100 text-red-800' :
              task.type === 'Test' ? 'bg-orange-100 text-orange-800' :
              task.type === 'Project' ? 'bg-purple-100 text-purple-800' :
              task.type === 'Assignment' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {task.type}
            </span>
            <span>{task.totalMarks} marks</span>
          </div>
        </div>
      </div>

      {/* Task Details */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="w-4 h-4" />
          <span>{task.grade} • {task.subject}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Calendar className="w-4 h-4" />
          <span>
            {isTaskOwner(task) ? 'Created' : 'Shared'} {' '}
            {task.createdAt ? new Date(task.createdAt).toLocaleDateString() : 'Unknown'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <FileText className="w-4 h-4" />
          <span>{task.questions?.length || 0} questions</span>
        </div>
      </div>

      {/* Topics */}
      <div className="mb-4">
        <div className="flex flex-wrap gap-1">
          {task.topics?.slice(0, 3).map((topic, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
            >
              {topic}
            </span>
          ))}
          {task.topics?.length > 3 && (
            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
              +{task.topics.length - 3} more
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => onTaskSelect(task)}
          className="px-3 py-2 bg-gray-100 text-purple-700 text-sm rounded-md hover:bg-gray-200 flex items-center justify-center"
        >
          <Eye className="w-4 h-4" />
        </button>

        <button
            onClick={() => onDuplicateTask?.(task)}  // Use the prop
            disabled={duplicatingTask === task.id}    // Use the prop
            className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Duplicate Task"
          >
            {duplicatingTask === task.id ? (  // Use the prop
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
            ) : (
              <Copy className="w-4 h-4" />
            )}
        </button>
        
        {/* Only show edit button for owned tasks */}
        {isTaskOwner(task) && (
          <button
            onClick={() => onEditTask(task)}
            className="px-3 py-2 bg-gray-100 text-purple-700 text-sm rounded-md hover:bg-gray-200 flex items-center justify-center"
            title="Edit task"
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
        
        {/* Only show delete button for owned tasks */}
        {isTaskOwner(task) && (
          <button
            onClick={() => onDeleteTask(task.id, task.name)}
            className="px-3 py-2 bg-red-100 text-red-700 text-sm rounded-md hover:bg-red-200 flex items-center justify-center"
            title="Delete task"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        {/* Show lock icon for shared tasks (read-only) */}
        {!isTaskOwner(task) && (
          <div className="px-3 py-2 bg-gray-100 text-gray-500 text-sm rounded-md flex items-center justify-center">
            <Lock className="w-4 h-4" title="Read-only (shared)" />
          </div>
        )}
      </div>

      {/* Ownership indicator */}
      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-500">
          {isTaskOwner(task) ? (
            <>
              Created by you
              {task.sharedWith?.length > 0 && (
                <span className="ml-2 text-green-600">
                  • Shared with {task.sharedWith.length} user{task.sharedWith.length !== 1 ? 's' : ''}
                </span>
              )}
            </>
          ) : (
            <span className="text-blue-600">Shared with you</span>
          )}
        </p>
      </div>
    </div>
  </div>
);

// Task Row Component for List View
const TaskRow = ({ task, isTaskOwner, onTaskSelect, onEditTask, onDeleteTask, onShareTask }) => (
  <tr className="bg-blue-50 hover:bg-gray-50">
    <td className="px-4 py-4">
      <div>
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium text-gray-900">{task.name}</div>
          {!isTaskOwner(task) && (
            <Share2 className="w-4 h-4 text-blue-500" title="Shared with you" />
          )}
          {isTaskOwner(task) && task.sharedWith?.length > 0 && (
            <Users className="w-4 h-4 text-green-500" title={`Shared with ${task.sharedWith.length} users`} />
          )}
        </div>
        <div className="text-sm text-gray-500">
          {task.topics?.slice(0, 2).join(', ')}
          {task.topics?.length > 2 && ` +${task.topics.length - 2} more`}
        </div>
      </div>
    </td>
    <td className="px-6 py-4">
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
        task.type === 'Exam' ? 'bg-red-100 text-red-800' :
        task.type === 'Test' ? 'bg-orange-100 text-orange-800' :
        task.type === 'Project' ? 'bg-purple-100 text-purple-800' :
        task.type === 'Assignment' ? 'bg-blue-100 text-blue-800' :
        'bg-gray-100 text-gray-800'
      }`}>
        {task.type}
      </span>
    </td>
    <td className="px-6 py-4 text-sm text-gray-900">
      <div>{task.grade}</div>
      <div className="text-gray-500">{task.subject}</div>
    </td>
    <td className="px-6 py-4 text-sm text-gray-900">
      {task.totalMarks}
    </td>
    <td className="px-6 py-4 text-sm text-gray-500">
      {isTaskOwner(task) ? 'Owner' : 'Shared'}
    </td>
    <td className="px-6 py-4 text-sm font-medium">
      <div className="flex items-center gap-2">
        <button
          onClick={() => onTaskSelect(task)}
          className="text-blue-600 hover:text-blue-900"
          title="View Details"
        >
          <Eye className="w-4 h-4" />
        </button>
        
        {isTaskOwner(task) && (
          <>
            <button
              onClick={() => onShareTask(task)}
              className="text-green-600 hover:text-green-900"
              title="Share task"
            >
              <UserPlus className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => onEditTask(task)}
              className="text-gray-600 hover:text-gray-900"
              title="Edit task"
            >
              <Edit className="w-4 h-4" />
            </button>
            
            <button
              onClick={() => onDeleteTask(task.id, task.name)}
              className="text-red-600 hover:text-red-900"
              title="Delete task"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </>
        )}

        {!isTaskOwner(task) && (
          <Lock className="w-4 h-4 text-gray-400" title="Read-only (shared)" />
        )}
      </div>
    </td>
  </tr>
);

// Sharing Modal Component
const SharingModal = ({ task, shareableUsers, onClose, onToggleShare, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredUsers = shareableUsers.filter(user =>
    `${user.title} ${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.district?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Share Task</h3>
              <p className="text-sm text-gray-500 mt-1">"{task.name}"</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 flex-1 overflow-hidden flex flex-col">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search educators..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Current sharing status */}
          {task.sharedWith?.length > 0 && (
            <div className="mb-4 p-3 bg-green-50 rounded-md">
              <p className="text-sm text-green-800">
                Currently shared with {task.sharedWith.length} educator{task.sharedWith.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}

          {/* Users list */}
          <div className="flex-1 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No educators found to share with</p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredUsers.map(user => {
                  const isShared = task.sharedWith?.includes(user.id);
                  return (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-md hover:bg-gray-50"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {user.title} {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.position} • {user.schoolName || user.district}
                        </div>
                        <div className="text-xs text-gray-400">
                          {user.shareType} connection
                        </div>
                      </div>
                      <button
                        onClick={() => onToggleShare(user.id, isShared)}
                        disabled={loading}
                        className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          isShared
                            ? 'bg-red-100 text-red-700 hover:bg-red-200'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                        ) : (
                          <>
                            {isShared ? (
                              <>
                                <X className="w-4 h-4" />
                                Unshare
                              </>
                            ) : (
                              <>
                                <Check className="w-4 h-4" />
                                Share
                              </>
                            )}
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TasksList;